import { z } from 'zod'
import { parsePrevisionModal } from './-prevision/prevision-model'

/**
 * Recherche d'URL de l'onglet Pilotage (rubrique Prévisions) : le périmètre, les cuisines centrales,
 * la plage de dates, le jour choisi, la modale ouverte, la simulation affichée, l'exclusion des
 * projets et la catégorie de la carte Projection. Toute valeur illisible retombe sur son défaut : un
 * lien abîmé ouvre l'onglet, jamais une erreur. Les seuils d'alerte ne sont plus
 * dans l'URL : ce sont les seuils globaux, réglés dans la modale Réglages (lot R-REG).
 */

/** Périmètre « tout le réseau », borné au périmètre boutique de l'affectation par le serveur. */
export const PILOTAGE_RESEAU = 'reseau'
/** `reseau`, ou l'identifiant d'une boutique. */
export type PilotagePerimetre = typeof PILOTAGE_RESEAU | number

/** Valeurs par défaut, retirées de l'URL (`stripSearchParams`) pour garder des liens courts. */
export const PILOTAGE_SEARCH_DEFAULTS = {
  perimetre: PILOTAGE_RESEAU,
} as const satisfies { perimetre: PilotagePerimetre }

export const pilotageSearchSchema = z.object({
  perimetre: z
    .union([z.literal(PILOTAGE_RESEAU), z.number().int().positive()])
    .default(PILOTAGE_SEARCH_DEFAULTS.perimetre)
    .catch(PILOTAGE_SEARCH_DEFAULTS.perimetre),
  /** Cuisines centrales du filtre de l'écran (contrat R-FILTRES § 2) ; absent : toutes. */
  cuisines: z.array(z.number().int().positive()).max(200).optional().catch(undefined),
  /** Catégorie de la carte Projection, et d'elle seule (R-FILTRES § 3) ; absent : toutes. */
  projectionCategorie: z.number().int().positive().optional().catch(undefined),
  /**
   * Plage de dates de l'écran (contrat R-PERIODE) ; absente : les 7 jours par défaut. La lecture ne
   * la suit qu'une fois contrôlée (`periodeDuLien`).
   */
  du: z.iso.date().optional().catch(undefined),
  au: z.iso.date().optional().catch(undefined),
  /** Modale ouverte : `categorie:12`, `reference:12:3760373405068` ou `reglages`. */
  modale: z.string().trim().min(1).max(120).optional().catch(undefined),
  /** Jour choisi (serpent, modale) ; absent : le jour de la prochaine livraison. */
  jour: z.iso.date().optional().catch(undefined),
  /**
   * Simulation de l'admin affichée sur l'écran (contrat R-SIM) : un rechargement la retrouve tant
   * qu'elle vit (30 min après son calcul). Lisible par son seul auteur : chez un autre admin, le
   * serveur répond 404 et l'écran revient aux prévisions officielles.
   */
  simulation: z.uuid().optional().catch(undefined),
  /**
   * « Inclure les projets » désactivé (contrat R-REG-2) : les boutiques en ouverture sortent des vues
   * réseau. Affichage seulement ; absent, l'écran les compte.
   */
  projets: z.literal(false).optional().catch(undefined),
  // ─── Anciens liens, relus pour être redirigés ───
  /** Ancienne vue : `reappro` ou `prevision`. */
  vue: z.string().optional().catch(undefined),
  /** Ancienne livraison de la vue Réappro : devient le jour choisi. */
  livraison: z.iso.date().optional().catch(undefined),
  /** Ancienne zone de modale en pleine largeur. */
  plein: z.string().optional().catch(undefined),
  /** Anciens seuils du Sélecteur (écart S-1, surproduction) : remplacés par les seuils globaux. */
  seuil: z.unknown().optional().catch(undefined),
  surprod: z.unknown().optional().catch(undefined),
})

export type PilotageSearch = z.infer<typeof pilotageSearchSchema>

/** Ce qu'efface la fermeture d'une modale : la modale seule, le jour choisi reste. */
export const PILOTAGE_MODAL_RESET = { modale: undefined } as const satisfies Partial<PilotageSearch>

/** Clé de la modale Réglages (seuils d'alerte, moteur, historique). */
export const PILOTAGE_REGLAGES_MODAL = 'reglages'
/** Ancienne clé de la modale des réglages du moteur : ouvre la modale Réglages. */
const PILOTAGE_MOTEUR_MODAL_ANCIENNE = 'moteur'

/**
 * Lien d'avant l'écran unique (vue Réappro, livraison, zone agrandie, modale d'une tuile ou d'une
 * ligne Réappro, seuils du Sélecteur, modale du moteur) : la recherche à laquelle le rediriger,
 * sans ces clés, la livraison devenue le jour choisi ; `null` pour un lien de l'écran unique.
 */
export function rechercheRedirigee(search: PilotageSearch): PilotageSearch | null {
  const moteur = search.modale === PILOTAGE_MOTEUR_MODAL_ANCIENNE
  const modaleAncienne =
    search.modale !== undefined &&
    search.modale !== PILOTAGE_REGLAGES_MODAL &&
    !moteur &&
    parsePrevisionModal(search.modale) === null
  if (
    search.vue === undefined &&
    search.livraison === undefined &&
    search.plein === undefined &&
    search.seuil === undefined &&
    search.surprod === undefined &&
    !moteur &&
    !modaleAncienne
  ) {
    return null
  }
  let modale = modaleAncienne ? undefined : search.modale
  if (moteur) modale = PILOTAGE_REGLAGES_MODAL
  return {
    perimetre: search.perimetre,
    modale,
    jour: search.jour ?? search.livraison,
    simulation: search.simulation,
    projets: search.projets,
    cuisines: search.cuisines,
    du: search.du,
    au: search.au,
    projectionCategorie: search.projectionCategorie,
  }
}
